import { useTranslation } from 'react-i18next'

import { CustomerSmsBadge, NumberHealthBadge } from '#/entities/number-health'
import type { CustomerDeliverability } from '#/entities/number-health'
import { ArrowLeftIcon, MoreVerticalIcon, PhoneIcon } from '#/shared/ui/icons'

import { getInitials } from '../../lib/avatar-tone'
import type { ConversationContact } from '../../model/conversation'
import type { ActionTarget } from '../../model/use-conversation-actions'
import { useStartCall } from '../../model/use-phone-panel-call'
import { ActivityRowMenu } from '../activity-row-menu'
import { CallDisabledHint } from '../call-disabled-hint'
import { CopyableNumber } from '../copyable-number'

/** The header menu drops unread + select — they belong to the list rows, not
 *  an open thread. */
const HEADER_MENU_HIDDEN = ['unread', 'select']

/** Blocked threads collapse the menu to copy number / open order / delete. */
const BLOCKED_MENU_HIDDEN = ['unread', 'favorite', 'mute', 'select', 'block']

interface ConversationHeaderProps {
  contact: ConversationContact
  /** Resolved counterparty customer with inline SMS deliverability; `null` =
   *  confirmed customer-less (badge falls back to the number-health lookup),
   *  `undefined` = still resolving (no badge yet). */
  customer?: CustomerDeliverability | null
  /** Thread/number the more-menu actions apply to. */
  menuTarget: ActionTarget
  starred?: boolean
  blocked?: boolean
  /** Opens the thread's order; set only when there is exactly one. */
  onOpenOrder?: () => void
  /** Opens the in-thread search; omitted when the thread can't be searched
   *  (no conversation id — a number-opened thread from calls / favorites). */
  onSearch?: () => void
  onBack?: () => void
}

export function ConversationHeader({
  contact,
  customer,
  menuTarget,
  starred,
  blocked,
  onOpenOrder,
  onSearch,
  onBack,
}: ConversationHeaderProps) {
  const { t } = useTranslation()
  return (
    <div className="flex h-16 items-center justify-between border-b border-warm-100 px-4 py-3">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label={t('phonePanel.conversation.back')}
          className="group flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-paper-avatar text-gray-900 md:size-8"
        >
          <ArrowLeftIcon className="size-4 transition-transform duration-200 group-hover:-translate-x-0.5" />
        </button>
      )}
      <div className="flex min-w-0 items-center gap-2">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-avatar-blue-soft text-xs font-semibold text-avatar-blue">
          {getInitials(contact.name)}
        </span>
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-semibold text-gray-900">
            {contact.name}
          </span>
          {contact.phone ? (
            <div className="flex min-w-0 items-center gap-1">
              <CopyableNumber
                number={contact.phone}
                className="block max-w-full truncate text-left text-2xs text-gray-300"
              />
              {customer === null ? (
                // Confirmed customer-less number — only case that still uses
                // the batched number-health lookup.
                <NumberHealthBadge phone={contact.phone} />
              ) : (
                <CustomerSmsBadge customer={customer} />
              )}
            </div>
          ) : null}
        </div>
      </div>
      <HeaderActions
        phone={contact.phone}
        menuTarget={menuTarget}
        starred={starred}
        blocked={blocked}
        onOpenOrder={onOpenOrder}
        onSearch={onSearch}
      />
    </div>
  )
}

/**
 * Right cluster: call, and the overflow menu.
 *
 * Search lives *inside* the menu rather than beside it. Calling is the one
 * action worth a permanent chip in a 16-unit-tall header next to the
 * counterparty's name and number; a third chip crowded that identity for an
 * action reached a handful of times a session. It is the menu's first row, so
 * it is still one open away.
 */
function HeaderActions({
  phone,
  menuTarget,
  starred,
  blocked,
  onOpenOrder,
  onSearch,
}: {
  phone: string
  menuTarget: ActionTarget
  starred?: boolean
  blocked?: boolean
  onOpenOrder?: () => void
  onSearch?: () => void
}) {
  const { t } = useTranslation()
  const { call, ready, disabledReason } = useStartCall()
  return (
    <div className="flex shrink-0 items-center gap-2">
      {!blocked && (
        <CallDisabledHint reason={disabledReason}>
          <button
            type="button"
            onClick={() => call(phone)}
            disabled={!ready}
            aria-label={t('phonePanel.conversation.call')}
            className="flex size-10 cursor-pointer items-center justify-center rounded-full bg-avatar-green-soft text-avatar-green disabled:cursor-not-allowed disabled:opacity-40 md:size-8"
          >
            <PhoneIcon className="size-4" />
          </button>
        </CallDisabledHint>
      )}
      <ActivityRowMenu
        itemId={menuTarget.conversationId ?? menuTarget.phone ?? ''}
        target={menuTarget}
        starred={starred}
        blocked={blocked}
        onOpenOrder={onOpenOrder}
        onSearch={onSearch}
        hiddenActions={blocked ? BLOCKED_MENU_HIDDEN : HEADER_MENU_HIDDEN}
        triggerIcon={MoreVerticalIcon}
        triggerClassName="size-10 bg-paper-avatar text-gray-500 md:size-8"
      />
    </div>
  )
}
